import { useCallback, useEffect, useMemo, useState } from "react";

import {
  useGetTextTemplatesCountQuery,
  useLazyGetTextTemplatesQuery
} from "services/text-templates";
import {
  TextTemplate,
  TextTemplateFilters
} from "services/types/text-templates.type";

// The backend caps every list request at 300 (CustomLimitOffsetPagination.
// max_limit). Browse (no search) loads a full 300-row page so practices with
// <=300 templates see their whole category tree on open; "Show more" pages the
// rest. Search uses a smaller page since matches are narrow and render as a flat
// list (ENG-1426).
export const TEMPLATE_PICKER_BROWSE_PAGE_SIZE = 300;
export const TEMPLATE_PICKER_SEARCH_PAGE_SIZE = 25;

const PROJECTION = "{id,name,type,category}";

type UseTemplatePickerArgs = {
  templateTypes?: number[];
  search: string;
};

export type UseTemplatePickerResult = {
  templates: TextTemplate[];
  totalCount?: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
};

/**
 * Fetches templates for the note/report picker with server-side search,
 * type filtering, and offset pagination. Pages accumulate into a single list
 * so the category tree grows as the user clicks "Show more"; changing the
 * search term or type filter resets back to the first page.
 */
export const useTemplatePicker = ({
  templateTypes,
  search
}: UseTemplatePickerArgs): UseTemplatePickerResult => {
  // Memoize on a primitive key so an unstable `templateTypes` array identity
  // from the parent doesn't retrigger the fetch effect on every render.
  const typeKey = templateTypes?.length ? templateTypes.join(",") : "";
  const filters = useMemo<Pick<TextTemplateFilters, "type" | "search">>(
    () => ({
      type: typeKey ? typeKey.split(",").map(Number) : undefined,
      search: search || undefined
    }),
    [typeKey, search]
  );

  const { data: totalCount } = useGetTextTemplatesCountQuery(filters);

  const [triggerGetTemplates] = useLazyGetTextTemplatesQuery();
  const [templates, setTemplates] = useState<TextTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchPage = useCallback(
    (offset: number): Promise<TextTemplate[]> => {
      const limit = filters.search
        ? TEMPLATE_PICKER_SEARCH_PAGE_SIZE
        : TEMPLATE_PICKER_BROWSE_PAGE_SIZE;
      return triggerGetTemplates({
        ...filters,
        query: PROJECTION,
        limit,
        offset
      })
        .unwrap()
        .catch(() => [] as TextTemplate[]);
    },
    [triggerGetTemplates, filters]
  );

  // Reset to the first page whenever the search term or type filter changes.
  useEffect(() => {
    let active = true;
    setIsLoading(true);
    fetchPage(0).then((page) => {
      if (active) {
        setTemplates(page);
        setIsLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);
    const page = await fetchPage(templates.length);
    setTemplates((prev) => [...prev, ...page]);
    setIsLoadingMore(false);
  }, [fetchPage, templates.length, isLoadingMore]);

  const hasMore =
    !isLoading && totalCount !== undefined && templates.length < totalCount;

  return {
    templates,
    totalCount,
    hasMore,
    isLoading,
    isLoadingMore,
    loadMore
  };
};
